import { useLogger } from '../../services/logging/logger-context';
import { useEffect, useRef, useState } from 'react';

const ZENDESK_READY_POLL_INTERVAL_MS = 100;
const ZENDESK_READY_TIMEOUT_MS = 15000;

/**
 * Hook that polls for window.zE to be defined.
 * Use this when rendering the chat on a page that may load before the Zendesk script (e.g. on direct reload).
 */
export const useZendeskChatReady = (): { isReady: boolean; timedOut: boolean } => {
  const [isReady, setIsReady] = useState(() => typeof window.zE !== 'undefined');
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (typeof window.zE !== 'undefined') {
      setIsReady(true);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (typeof window.zE !== 'undefined') {
        setIsReady(true);
        clearInterval(interval);
        return;
      }
      if (Date.now() - startTime >= ZENDESK_READY_TIMEOUT_MS) {
        setTimedOut(true);
        clearInterval(interval);
      }
    }, ZENDESK_READY_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return { isReady, timedOut };
};

interface ZendeskChatProps {
  zendeskKey: string;
}

/**
 * Component that loads and manages the Zendesk Web Widget
 * The widget script is loaded dynamically and provides methods to interact with the chat
 */
export const ZendeskChat = ({ zendeskKey }: ZendeskChatProps) => {
  const scriptLoaded = useRef(false);
  const { logger } = useLogger();
  useEffect(() => {
    // Only load the script once
    if (scriptLoaded.current) {
      return;
    }

    // Check if script already exists
    const existingScript = document.getElementById('ze-snippet');
    if (existingScript) {
      scriptLoaded.current = true;
      return;
    }

    // Configure Zendesk messenger before loading script
    window.zEMessenger = {
      autorender: false,
    };

    // Create and load the Zendesk script
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
    script.async = true;
    script.onload = () => {
      scriptLoaded.current = true;
    };

    script.onerror = () => {
      logger.error(
        'Failed to load Zendesk widget script',
        new Error('Failed to load Zendesk widget script'),
      );
    };

    document.body.appendChild(script);

    // Cleanup function
    return () => {
      // Don't remove the script on unmount as it's a global widget
      // The script will remain for the lifetime of the app
    };
  }, [zendeskKey]);

  return null;
};

/**
 * Conversation field type for Zendesk
 */
export interface ConversationField {
  id: string;
  value: string | number | boolean;
}

/** Zendesk conversation field IDs (configured in Zendesk admin) */
export const ZENDESK_FIELD_IDS = {
  SITE_PHONE_NUMBER: '47930453866395',
  FULL_NAME: '1260827619829',
  EMAIL: '1260827730090',
  PHONE_NUMBER: '26651557489947',
  CHARGER_NAME: '1260827730030',
  CHARGER_UUID: '47931861992475',
  DEFAULT_PAYMENT_METHOD_EXPIRED_STATUS: '47978809114523',
  CHARGER_IS_FREE: '48149879968923',
  CHARGER_PRICE_TYPE: '47931629083163',
  CHARGER_UNIT_PRICE: '47930775200155',
  CHARGER_UNDER_MAINTENANCE: '48150165621531',
  WHITE_LABELLING_ID: '47931106220059',
  WHITE_LABELLING_NAME: '47931024842907',
  USER_HAS_ACCESS: '48149945041307',
  REQUIRE_PAYMENT_METHOD: '48150067393307',
  USER_PRICE_ADJUSTMENT_TYPE: '47931444737179',
  USER_PRICE_ADJUSTMENT_VALUE: '47931580846491',
  HARDWARE_CHARGER_ID: '1260831885269',
  CHARGER_MODEL_TEXT: '1900008411604',
  CHARGER_MODEL_UUID: '47931818500891',
  INITIAL_CHARGER_PORT_1_STATUS: '47931916337691',
  CHARGER_PORT_1_CONNECTOR_TYPE: '47931935189659',
  CHARGER_PORT_1_MAX_POWER_KILOWATTS: '47932038747803',
  CHARGER_PORT_1_CONNECTOR_LOCATION: '47932043312411',
  INITIAL_CHARGER_PORT_2_STATUS: '47932116455067',
  CHARGER_PORT_2_CONNECTOR_TYPE: '47932312949787',
  CHARGER_PORT_2_MAX_POWER_KILOWATTS: '47932274586267',
  CHARGER_PORT_2_CONNECTOR_LOCATION: '47932314161947',
  INITIAL_CHARGER_PORT_3_STATUS: '47932492482971',
  CHARGER_PORT_3_CONNECTOR_TYPE: '47932579206427',
  CHARGER_PORT_3_MAX_POWER_KILOWATTS: '47932618256667',
  CHARGER_PORT_3_CONNECTOR_LOCATION: '47932514217627',
  SESSION_ID: '47932632432795',
  LOCATION_ID: '47932677971355',
  SITE_ADDRESS: '47932639728027',
  SITE_NAME: '47932694008731',
  COMPANY_NAME: '1260827747189',
  COMPANY_UUID: '47932698917531',
} as const;

/**
 * Sets conversation fields for the Zendesk chat
 */
export const setZendeskConversationFields = (conversationFields: ConversationField[]) => {
  if (window.zE) {
    window.zE('messenger:set', 'conversationFields', conversationFields);
  } else {
    console.warn('Zendesk widget not loaded yet');
  }
};

/**
 * Renders and opens the Zendesk chat widget in embedded mode
 */
export const renderZendeskChat = (
  targetElement: string,
  conversationFields?: ConversationField[],
) => {
  if (window.zE) {
    const container = document.querySelector(targetElement);
    if (!container) {
      console.warn(`Target element ${targetElement} not found`);
      return;
    }

    // Check if widget is already rendered by checking if container has Zendesk content
    const hasZendeskContent =
      container.children.length > 0 &&
      (container.querySelector('iframe') || container.querySelector('[class*="zendesk"]'));

    if (hasZendeskContent) {
      // Already rendered, set conversation fields if provided and open it
      if (conversationFields && conversationFields.length > 0) {
        setZendeskConversationFields(conversationFields);
      }
      window.zE('messenger', 'open');
    } else {
      // Set conversation fields before rendering if provided
      if (conversationFields && conversationFields.length > 0) {
        setZendeskConversationFields(conversationFields);
      }
      // Render in embedded mode
      window.zE('messenger', 'render', {
        mode: 'embedded',
        widget: {
          targetElement: targetElement,
        },
      });
    }
  } else {
    console.warn('Zendesk widget not loaded yet');
  }
};

/**
 * Opens the Zendesk chat widget
 */
export const openZendeskChat = () => {
  if (window.zE) {
    window.zE('messenger', 'open');
  } else {
    console.warn('Zendesk widget not loaded yet');
  }
};

/**
 * Closes the Zendesk chat widget
 */
export const closeZendeskChat = () => {
  if (window.zE) {
    window.zE('messenger', 'close');
  }
};

/**
 * Checks if Zendesk widget is loaded
 */
export const isZendeskLoaded = (): boolean => {
  return typeof window.zE !== 'undefined';
};
